import { useCallback } from "react";
import { Navigate, useSearchParams } from "react-router";
import { ssoApi } from "@entrilia/shared/api/sso";
import { RedirectKeys } from "@entrilia/shared/auth/authTypes";
import { getAuthSettings } from "@entrilia/shared/auth/settings";
import AuthenticationFailed from "@entrilia/shared/components/AuthenticationFailed";
import FullScreenLoader from "@entrilia/shared/components/fullScreenLoader/FullScreenLoader";
import NoAccessPage from "@entrilia/shared/components/NoAccessPage";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { handleRedirectUrlState } from "@entrilia/shared/utilities/handleRedirectUrlState";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import { pageRoutes } from "../routes";

const Authentication = () => {
  const [searchParams] = useSearchParams();
  const authCode = searchParams.get(RedirectKeys.Code);

  const acquireToken = useCallback(() => {
    const { clientId, cookieTarget } = getAuthSettings();

    return ssoApi.requestToken({
      clientId,
      redirectUri: `${window.location.origin}/${pageRoutes.oauth}/`,
      authCode: defined(authCode),
      idpType: "Microsoft",
      cookieTarget,
    });
  }, [authCode]);

  const [tokenResp, error] = useFetch(acquireToken, { disabled: !authCode });

  if (!authCode) {
    return <Navigate to="/" />;
  }

  if (error) {
    if (error.code === 401) {
      return <AuthenticationFailed />;
    }

    if (error.code === 403) {
      return <NoAccessPage />;
    }

    logError(error, "[Authentication] acquireToken");
    return <Navigate to={`/${pageRoutes.error}`} />;
  }

  if (tokenResp) {
    const redirected = handleRedirectUrlState();
    if (redirected) {
      return null;
    }

    return <Navigate to="/" />;
  }

  return <FullScreenLoader title="Please wait" subtitle="We're authorizing you..." />;
};

export default Authentication;
